import { useCallback } from 'react';
import { AddressDto } from 'api/actor';
import { ReferentialKey } from 'api/referential';
import { useAppSelector } from 'app/hooks';
import { selectReferentialData } from 'features/referentials/selectors/selectReferentialDataByKey';
import { createPim, CreatePimCommand } from '../api/pimsApi';
import { PimFormValues } from '../form/PimFormValues';

import { buildGerantCommand } from 'back-office/shared/components/gerant/gerantFields';
export const usePimForm = () => {
  const referentialsData = useAppSelector(selectReferentialData);

  const getReferentialId = useCallback((referentialKey: ReferentialKey, code?: string) => {
    if (!code) {
      return undefined;
    }
    return referentialsData[referentialKey].find(ref => ref.code === code)?.id;
  }, [referentialsData]);

  const buildAddressDto = useCallback((values: PimFormValues): AddressDto => ({
    line1: values.address,
    line2: values.additionalAddress || '',
    zipCode: values.postalCode,
    city: values.city,
    country: values.country,
    formattedAddress: `${values.address},${values.city},${values.country}`,
    latitude: values.latitude || undefined,
    longitude: values.longitude || undefined,
    googlePlaceId: values.googlePlaceId || undefined,
  }), []);

  const createPimFromForm = useCallback(
    async (values: PimFormValues) => {
      // Plateformes multiples : chaque mandant en partage au moins une.
      const plateformeIds = (values.plateformeIds ?? []).flatMap((code) => {
        const id = getReferentialId(ReferentialKey.Plateforme, code);
        return id ? [id] : [];
      });

      const command: CreatePimCommand = {
        companyName: values.companyName,
        address: buildAddressDto(values),
        gerant: buildGerantCommand(values, getReferentialId),
        phone: values.phone,
        registrationNumber: values.registrationNumber,
        capital: values.capital ?? 0,
        rcsCity: values.rcsCity,
        rcsNumber: values.rcsNumber,
        rcp: values.rcp || null,
        complaintEmail: values.complaintEmail,
        mandants: values.mandants,
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        username: values.username,
        password: values.password,
        plateformeIds,
      };

      return await createPim(command);
    },
    [buildAddressDto, getReferentialId]
  );

  return { createPim: createPimFromForm };
};
